interface RichTextNode {
  type?: string;
  text?: unknown;
  content?: unknown;
}

// The single fold over a ProseMirror/TipTap node tree — the one place that knows the
// doc shape. Both flatteners below parameterise it rather than re-walking the tree.
const foldNode = (
  node: RichTextNode,
  join: string,
  wrapBlock: (type: string | undefined, children: string) => string,
): string => {
  if (typeof node.text === 'string') {
    return node.text;
  }
  const children = Array.isArray(node.content)
    ? node.content.map(child => foldNode(child as RichTextNode, join, wrapBlock)).join(join)
    : '';
  return wrapBlock(node.type, children);
};

// Flattens a ProseMirror/TipTap rich-text document (or a plain string) to a single
// whitespace-normalised line of text — used for SEO meta descriptions where the source
// field is a structured doc rather than plain copy.
export const richTextToPlainText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    // A rich-text field may arrive as a JSON-encoded ProseMirror doc string — parse and
    // recurse so we extract the text rather than emitting the raw JSON.
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return richTextToPlainText(JSON.parse(trimmed));
      }
      catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const text = foldNode(value as RichTextNode, ' ', (_type, children) => children)
    .replace(/\s+/g, ' ')
    .trim();
  return text || undefined;
};

/**
 * Flatten a TipTap JSON string to plain text preserving block structure (one line per
 * paragraph/heading, list items bulleted) — used to word-diff two document versions.
 * Falls back to the raw string for non-JSON content (legacy plain-text terms).
 */
export const richTextToBlockText = (content: string | null | undefined): string => {
  if (!content) {
    return '';
  }

  try {
    return foldNode(JSON.parse(content) as RichTextNode, '', (type, children) => {
      switch (type) {
        case 'paragraph':
        case 'heading':
          return `${children}\n`;
        case 'listItem':
          return `• ${children.trimEnd()}\n`;
        default:
          return children;
      }
    }).replace(/\n{2,}/g, '\n').trim();
  }
  catch {
    return content;
  }
};
